import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import {
  createGroupedModelRuntimeState,
  createProgressModelRuntimeState,
  createSingleModelRuntimeState,
  createOperaRuntimeState,
  modelStateRegistry,
} from '@/utils/modelStateRegistry'

import meta from './meta'
import auth from './auth'
import cart from './cart'
import resolver from './resolver'
import webtest from './webtest'
import opera from './opera'
import predictor from './predictor'
import hazard from './hazard'
import safety from './safety'
import search from './search'
import toxprints from './toxprints'
import settings from '@/store/settings'
import stdizer from '@/store/stdizer'
import alerts from '@/store/alerts'
import chemicals from '@/store/chemicals'
import files from '@/store/files'
import spectra from '@/store/spectra'
import chet from '@/store/chet'
import amos from '@/store/amos'
import epiSuite from '@/store/epiSuite'
import toddsModels from '@/store/toddsModels'
import pfasCategories from '@/store/pfasCategories'
import pfasAtlas from '@/store/pfasAtlas'
import nccCategories from '@/store/nccCategories'
import arnCategories from '@/store/arnCategories'
import amenability from '@/store/amenability'

Vue.use(Vuex)

const transientPredictionNamespaces = new Set([
  'epiSuite',
  'webtest',
  'opera',
  'toddsModels',
  'pfasCategories',
  'pfasAtlas',
  'nccCategories',
  'arnCategories',
  'amenability',
])

const persistedPredictionMutationNames = new Set(['SET_METADATA'])

const getPersistedPredictionState = (stateSlice, runtimeStateFactory) => {
  return {
    metadata: stateSlice?.metadata ?? null,
    ...runtimeStateFactory(),
  }
}

const shouldPersistMutation = (mutation) => {
  const mutationType = mutation?.type || ''
  const [namespace, actionName] = mutationType.split('/')

  if (!transientPredictionNamespaces.has(namespace)) return true

  return persistedPredictionMutationNames.has(actionName)
}

const stripTransientReports = (state) => {
  const { searchResult: _searchResult, ...searchState } = state?.search || {}

  const {
    toxprintsResult: _toxprintsResult,
    toxprintsSelectedChemical: _toxprintsSelectedChemical,
    ...toxprintsState
  } = state?.toxprints || {}
  const {
    reportData: _hazardReportData,
    loading: _hazardLoading,
    errorMessage: _hazardErrorMessage,
    ...hazardState
  } = state?.hazard || {}
  const { hideND: _hazardHideND, ...hazardReportFilters } = hazardState.hazardReportFilters || {}
  const {
    stateSafetyResult: _stateSafetyResult,
    stateSafetyOptions: _stateSafetyOptions,
    loading: _safetyLoading,
    errorMessage: _safetyErrorMessage,
    ...safetyState
  } = state?.safety || {}
  const {
    stateStdizerResult: _stateStdizerResult,
    stateStdizerOptions: _stateStdizerOptions,
    ...stdizerState
  } = state?.stdizer || {}
  const {
    stateAlertsResult: _stateAlertsResult,
    stateAlertsOptions: _stateAlertsOptions,
    ...alertsState
  } = state?.alerts || {}

  return {
    ...(state ? state : {}),
    search: searchState,
    toxprints: toxprintsState,
    hazard: {
      ...hazardState,
      hazardReportFilters,
    },
    safety: safetyState,
    stdizer: stdizerState,
    alerts: alertsState,
  }
}

const clearRehydratedReports = (store) => {
  if (store.state?.search?.searchResult) {
    store.commit('RESET_SEARCH_RESULT')
  }

  if (store.state?.toxprints?.toxprintsResult?.chemicals?.length || store.state?.toxprints?.toxprintsSelectedChemical) {
    store.commit('resetToxprintsResult')
  }

  if (store.state?.hazard?.reportData) {
    store.commit('RESET_HAZARD_REPORT')
  }

  if (store.state?.safety?.stateSafetyResult) {
    store.commit('resetSafetyResult')
  }

  if (store.state?.stdizer?.stateStdizerResult) {
    store.commit('resetStdizerResult')
  }

  if (store.state?.alerts?.stateAlertsResult) {
    store.commit('resetAlertsResult')
  }
}

export default new Vuex.Store({
  strict: false,
  state: {},
  getters: {},
  mutations: {},
  actions: {},
  modules: {
    meta,
    auth,
    cart,
    chet,
    amos,
    epiSuite,
    resolver,
    webtest,
    opera,
    predictor,
    hazard,
    safety,
    search,
    toxprints,
    settings,
    stdizer,
    alerts,
    chemicals,
    files,
    spectra,
    toddsModels,
    pfasCategories,
    pfasAtlas,
    nccCategories,
    arnCategories,
    amenability,
  },

  plugins: [
    createPersistedState({
      filter: shouldPersistMutation,
      storage: {
        getItem: function (key) {
          try {
            return localStorage.getItem(key)
          } catch (err) {
            //
          }
        },
        setItem: function (key, value) {
          try {
            localStorage.setItem(key, value)
          } catch (err) {
            //
          }
        },
        removeItem: function (key) {
          try {
            localStorage.removeItem(key)
          } catch (err) {
            //
          }
        },
      },
      reducer: (state) => {
        const baseState = stripTransientReports(state)

        return {
          ...baseState,
          epiSuite: getPersistedPredictionState(
            {
              ...modelStateRegistry.epiSuite.createState(),
              ...(baseState.epiSuite ? baseState.epiSuite : {}),
            },
            createProgressModelRuntimeState,
          ),
          webtest: getPersistedPredictionState(
            {
              ...modelStateRegistry.webtest.createState(),
              ...(baseState.webtest ? baseState.webtest : {}),
            },
            createSingleModelRuntimeState,
          ),
          opera: getPersistedPredictionState(
            {
              ...modelStateRegistry.opera.createState(),
              ...(baseState.opera ? baseState.opera : {}),
            },
            createOperaRuntimeState,
          ),
          toddsModels: getPersistedPredictionState(
            {
              ...modelStateRegistry.groupedModel.createState(),
              ...(baseState.toddsModels ? baseState.toddsModels : {}),
            },
            createGroupedModelRuntimeState,
          ),
          pfasCategories: getPersistedPredictionState(
            {
              ...modelStateRegistry.groupedModel.createState(),
              ...(baseState.pfasCategories ? baseState.pfasCategories : {}),
            },
            createGroupedModelRuntimeState,
          ),
          pfasAtlas: getPersistedPredictionState(
            {
              ...modelStateRegistry.groupedModel.createState(),
              ...(baseState.pfasAtlas ? baseState.pfasAtlas : {}),
            },
            createGroupedModelRuntimeState,
          ),
          nccCategories: getPersistedPredictionState(
            {
              ...modelStateRegistry.groupedModel.createState(),
              ...(baseState.nccCategories ? baseState.nccCategories : {}),
            },
            createGroupedModelRuntimeState,
          ),
          arnCategories: getPersistedPredictionState(
            {
              ...modelStateRegistry.groupedModel.createState(),
              ...(baseState.arnCategories ? baseState.arnCategories : {}),
            },
            createGroupedModelRuntimeState,
          ),
          amenability: getPersistedPredictionState(
            {
              ...modelStateRegistry.groupedModel.createState(),
              ...(baseState.amenability ? baseState.amenability : {}),
            },
            createGroupedModelRuntimeState,
          ),
        }
      },
    }),
    clearRehydratedReports,
  ],
})
